import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { postApiAuditEvents } from "@/lib/api/generated/audit/audit";
import type { AuditEventDto } from "@/lib/api/generated/control-api.schemas";
import {
  usePostApiRuntimeTargetDeploy,
  usePostApiRuntimeTargetProvision,
  usePostApiRuntimeTargetReconcile,
  useGetApiRuntimeTarget,
} from "@/lib/api/generated/runtime-targets/runtime-targets";
import { useGetApiLiveSnapshot } from "@/lib/api/generated/state/state";
import { useGetApiTenantsTenantId } from "@/lib/api/generated/tenants/tenants";
import { usePostApiSnapshotApply } from "@/lib/api/generated/workflows/workflows";
import { StatusBadge } from "@/components/shared/status-badge";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { formatTimestamp, toastMutationError, truncateId } from "@/lib/utils/formatting";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { queryKeys } from "@/lib/query/keys";
import { RuntimeCronJobsCard } from "@/features/runtime-cron-jobs/runtime-cron-jobs-card";
import { RuntimeMediaAudioCard } from "@/features/runtime-targets/runtime-media-audio-card";
import type { TenantLiveSnapshotRecord } from "@/lib/types/live-snapshot";

const DEPLOYMENT_EVENT_TYPES = new Set([
  "runtime_target.reconciled",
  "snapshot.applied",
  "snapshot.apply_rejected",
]);

export function RuntimeTargetsPage() {
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const { data: tenant } = useGetApiTenantsTenantId(tenantId ?? "", {
    query: { enabled: Boolean(tenantId) },
  });
  const { data: target } = useGetApiRuntimeTarget({
    query: {
      enabled: Boolean(tenantId),
      queryKey: queryKeys.tenantRuntimeTarget(tenantId ?? "none"),
    },
  });
  const { data: liveSnapshotData } = useGetApiLiveSnapshot({
    query: {
      enabled: Boolean(tenantId),
      queryKey: queryKeys.liveSnapshot(tenantId ?? "none"),
    },
  });
  const liveSnapshot = liveSnapshotData as unknown as TenantLiveSnapshotRecord | null | undefined;
  const { data: auditEvents = [], isLoading: auditEventsLoading } = useQuery({
    queryKey: ["audit-events", tenantId, "runtime", 12],
    queryFn: () =>
      postApiAuditEvents({
        resourceId: tenantId!,
        limit: 12,
      }),
    enabled: Boolean(tenantId),
  });
  const provisionMutation = usePostApiRuntimeTargetProvision();
  const reconcileMutation = usePostApiRuntimeTargetReconcile();
  const applySnapshotMutation = usePostApiSnapshotApply();
  const deployMutation = usePostApiRuntimeTargetDeploy();
  const autoProvisionRequested = useRef(false);
  useDocumentTitle("Runtime");

  const invalidateRuntimeState = async () => {
    if (!tenantId) {
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantRuntimeTarget(tenantId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.liveSnapshot(tenantId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.state }),
    ]);
  };

  useEffect(() => {
    if (!tenantId || target || autoProvisionRequested.current) {
      return;
    }
    autoProvisionRequested.current = true;
    void provisionMutation.mutateAsync({ data: {} }).catch(() => undefined);
  }, [tenantId, target, provisionMutation]);

  const onProvision = async () => {
    if (!tenantId) {
      return;
    }
    try {
      await provisionMutation.mutateAsync({ data: {} });
      await invalidateRuntimeState();
      toast.success(target ? "Runtime target refreshed" : "Workspace runtime initialized");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to initialize workspace runtime"));
    }
  };

  const onReconcile = async () => {
    try {
      await reconcileMutation.mutateAsync({ data: {} });
      await invalidateRuntimeState();
      toast.success("Runtime reconciled");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to reconcile runtime"));
    }
  };

  const onDeploy = async () => {
    try {
      await deployMutation.mutateAsync({ data: {} });
      await invalidateRuntimeState();
      toast.success("Runtime deployed");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to deploy runtime"));
    }
  };

  const onApplySnapshot = async () => {
    if (!target) {
      toast.error("No runtime target configured. Save a runtime target first.");
      return;
    }
    try {
      await applySnapshotMutation.mutateAsync({
        data: {
          workspaceRootDir: target.workspaceRootDir,
          generatedDir: target.generatedDir,
          snapshotPath: target.snapshotPath,
        },
      });
      await invalidateRuntimeState();
      toast.success("Snapshot applied");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to apply snapshot"));
    }
  };
  const busy =
    provisionMutation.isPending ||
    reconcileMutation.isPending ||
    applySnapshotMutation.isPending ||
    deployMutation.isPending;
  const deploymentEvents = auditEvents
    .filter((event) => DEPLOYMENT_EVENT_TYPES.has(event.eventType))
    .slice()
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  const workspaceStatus = tenant?.workspaceStatus ?? (target ? "ready" : undefined);
  const workspaceMessage = describeWorkspaceState(
    workspaceStatus,
    tenant?.workspaceStatusMessage ?? undefined,
  );

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle>Workspace Runtime</CardTitle>
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Tenants should not hand-configure runtime coordinates. The platform provisions and
              maintains the workspace runtime, then exposes health and deployment controls here.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onProvision}
              disabled={busy}
            >
              {provisionMutation.isPending
                ? "Initializing..."
                : target
                  ? "Recreate Defaults"
                  : "Initialize Workspace"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onReconcile}
              disabled={busy || !target}
            >
              {reconcileMutation.isPending ? "Reconciling..." : "Reconcile Runtime"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onApplySnapshot}
              disabled={busy || !target}
            >
              {applySnapshotMutation.isPending ? "Applying..." : "Apply Snapshot"}
            </Button>
            <Button type="button" size="sm" onClick={onDeploy} disabled={busy || !target}>
              {deployMutation.isPending ? "Deploying..." : "Deploy Latest"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {target ? (
            <div className="grid gap-4 md:grid-cols-2">
              <DisplayField label="Runtime Key" value={target.runtimeKey} />
              <DisplayField label="Base URL" value={target.baseUrl} mono />
              <DisplayField label="Workspace Root Dir" value={target.workspaceRootDir} mono />
              <DisplayField label="Generated Dir" value={target.generatedDir ?? "Platform default"} mono />
              <DisplayField label="Snapshot Path" value={target.snapshotPath ?? "Platform default"} mono />
              <DisplayField label="Credentials Ref" value={target.credentialsRef ?? "Platform managed"} mono />
            </div>
          ) : (
            <div className="rounded-lg border p-4 text-sm" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2">
                {workspaceStatus ? <StatusBadge status={workspaceStatus} /> : null}
                <span style={{ color: "var(--text-strong)" }}>Managed runtime setup</span>
              </div>
              <p className="mt-2" style={{ color: "var(--muted-foreground)" }}>
                {workspaceMessage}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <RuntimeMediaAudioCard />

      <Card>
        <CardHeader>
          <CardTitle>Deployment Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 text-xs md:grid-cols-2">
            <Metric label="Deployment Status">
              {target?.deploymentStatus ? (
                <StatusBadge status={target.deploymentStatus === "reconciled" ? "running" : target.deploymentStatus} />
              ) : (
                "—"
              )}
            </Metric>
            <Metric label="Runtime Health">
              {target?.runtimeHealth ? <StatusBadge status={target.runtimeHealth} /> : "—"}
            </Metric>
            <Metric label="Desired Image">
              <code>{target?.desiredImageRef ?? "—"}</code>
            </Metric>
            <Metric label="Applied Image">
              <code>{target?.appliedImageRef ?? "—"}</code>
            </Metric>
            <Metric label="Last Reconciled At">
              <span>—</span>
            </Metric>
            <Metric label="Last Deployed At">
              <span>{formatTimestamp(target?.lastDeployedAt ?? undefined)}</span>
            </Metric>
            <Metric label="Last Seen At">
              <span>{formatTimestamp(target?.lastSeenAt ?? undefined)}</span>
            </Metric>
          </div>
        </CardContent>
      </Card>

      {tenantId ? <RuntimeCronJobsCard tenantId={tenantId} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>Recent Deployment Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {auditEventsLoading ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Loading deployment activity...
            </p>
          ) : deploymentEvents.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Deployment and snapshot audit events will appear here after reconcile or deploy actions run.
            </p>
          ) : (
            <div className="flex flex-col divide-y" style={{ borderColor: "var(--border)" }}>
              {deploymentEvents.map((event) => (
                <div key={event.id} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                      {describeDeploymentEvent(event)}
                    </span>
                    <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {formatTimestamp(event.createdAt)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span style={{ color: "var(--muted-foreground)" }}>
                      Event: <code>{event.eventType}</code>
                    </span>
                    <span style={{ color: "var(--muted-foreground)" }}>
                      Actor: <code>{event.actor.displayName ?? event.actor.actorId}</code>
                    </span>
                  </div>
                  {event.details && (
                    <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {summarizeAuditDetails(event.details)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {liveSnapshot?.snapshot && (
        <Card>
          <CardHeader>
            <CardTitle>Snapshot Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 text-xs md:grid-cols-2">
              <Metric label="Desired Generation">
                <code>{liveSnapshot.snapshot.generation ?? "—"}</code>
              </Metric>
              <Metric label="Desired Hash">
                <code>{truncateValue(liveSnapshot.snapshot.snapshotHash)}</code>
              </Metric>
              <Metric label="Apply Status">
                {getLastApplyStatus(liveSnapshot.lastApplyResponse) ? (
                  <StatusBadge status={getLastApplyStatus(liveSnapshot.lastApplyResponse) ?? "pending"} />
                ) : (
                  "—"
                )}
              </Metric>
              <Metric label="Generated At">
                <span>{formatTimestamp(liveSnapshot.snapshot.generatedAt)}</span>
              </Metric>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function getLastApplyStatus(lastApplyResponse: unknown): string | undefined {
  if (!lastApplyResponse || typeof lastApplyResponse !== "object") {
    return undefined;
  }
  if (!("status" in lastApplyResponse)) {
    return undefined;
  }
  const status = (lastApplyResponse as { status?: unknown }).status;
  return typeof status === "string" ? status : undefined;
}

function truncateValue(value: string | undefined): string {
  if (!value) {
    return "—";
  }
  return truncateId(value, 18);
}

function describeDeploymentEvent(event: AuditEventDto): string {
  switch (event.eventType) {
    case "runtime_target.reconciled":
      return "Runtime reconciled";
    case "snapshot.applied":
      return "Snapshot applied";
    case "snapshot.apply_rejected":
      return "Snapshot apply rejected";
    default:
      return event.eventType;
  }
}

function summarizeAuditDetails(details: Record<string, unknown>): string {
  const message = typeof details.message === "string" ? details.message : undefined;
  const action = typeof details.action === "string" ? details.action : undefined;
  const generation =
    typeof details.generation === "number" ? `generation ${details.generation}` : undefined;
  const status = typeof details.status === "string" ? details.status : undefined;
  const errorMessage =
    typeof details.errorMessage === "string" ? details.errorMessage : undefined;
  return [message, action ? `action ${action}` : undefined, generation, status, errorMessage]
    .filter((value): value is string => Boolean(value))
    .join(" · ");
}

function Metric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span style={{ color: "var(--muted-foreground)" }}>{label}</span>
      <div className="mt-1 text-sm" style={{ color: "var(--text-strong)" }}>
        {children}
      </div>
    </div>
  );
}

function DisplayField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      <div
        className={`rounded-sm border px-3 py-2 text-sm ${mono ? "font-mono" : ""}`}
        style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
      >
        {value}
      </div>
    </div>
  );
}

function describeWorkspaceState(
  status: string | undefined,
  message: string | undefined,
): string {
  if (message?.trim()) {
    return message.trim();
  }
  if (status === "preparing") {
    return "The platform is preparing this tenant runtime in the background. You should not need to enter runtime coordinates manually.";
  }
  if (status === "ready") {
    return "This tenant runtime is managed by platform defaults. Use reconcile and deploy when you need to refresh the managed environment.";
  }
  if (status === "needs_attention") {
    return "Automatic runtime setup needs operator attention. The tenant should not fill infrastructure values here.";
  }
  return "The platform should provision a tenant runtime automatically. If this page is still empty, the environment defaults are not fully wired yet.";
}
